import { useState, useEffect, useRef, useCallback } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const G = 9.81, L = 2.34, M_BOB = 0.5;
const MU = 3.986e14, RE = 6371e3, M_SAT = 12, ISP_CG = 70, G0 = 9.81;
const DEG15 = 15 * Math.PI / 180;

// ─── Pendulum: correct small-angle, fires ONLY at TO-side turning point ───────
// TO side = positive theta (right side). Repulsion pushes it further right (outward).
function makePend() {
  const h0 = L * (1 - Math.cos(DEG15));
  return {
    theta: DEG15, omega: 0,
    h: h0, maxH_to: h0,   // track TO-side max only
    passes: 0,
    history: [{ n: 0, h: h0 * 100 }],
    fired: false, cooldown: 0,
    propG: 0   // propellant equivalent (cold gas grams)
  };
}

function stepPend(s, dt, J, beta) {
  let { theta, omega, maxH_to, passes, history, cooldown, propG } = s;
  let fired = false;

  // Cooldown countdown
  if (cooldown > 0) cooldown = Math.max(0, cooldown - dt);

  // Fire condition: at TO-side turning point only
  // TO side = theta > 0, omega ≈ 0 (turning from positive to negative)
  const atToTurning = theta > 0.10 && omega > -0.05 && omega < 0.05 && cooldown === 0;

  if (atToTurning && J > 0) {
    // Repulsion at turning point: bob stationary → all J → outward velocity
    // Outward = positive direction = same sign as theta
    const dv = J / M_BOB;  // tangential velocity added
    omega += dv / L;        // outward angular velocity
    fired = true;
    cooldown = 0.4;         // prevent re-firing immediately
    passes++;
    // Propellant: equivalent cold gas for same impulse at satellite scale
    const dv_sat = J / M_SAT;
    const dm = M_SAT * (1 - Math.exp(-dv_sat / (ISP_CG * G0)));
    propG += dm * 1000;
  }

  // RK4 integration with damping
  const damp = 2 * beta;
  const f = (t, w) => -(G / L) * Math.sin(t) - damp * w;
  const k1w = dt * f(theta, omega), k1t = dt * omega;
  const k2w = dt * f(theta + k1t / 2, omega + k1w / 2), k2t = dt * (omega + k1w / 2);
  const k3w = dt * f(theta + k2t / 2, omega + k2w / 2), k3t = dt * (omega + k2w / 2);
  const k4w = dt * f(theta + k3t, omega + k3w), k4t = dt * (omega + k3w);
  theta += (k1t + 2*k2t + 2*k3t + k4t) / 6;
  omega += (k1w + 2*k2w + 2*k3w + k4w) / 6;

  const h = L * (1 - Math.cos(theta));
  // Only track TO-side max (positive theta)
  if (theta > 0) maxH_to = Math.max(maxH_to, h);

  const newHistory = fired
    ? [...history.slice(-79), { n: passes, h: maxH_to * 100 }]
    : history;

  return { ...s, theta, omega, h, maxH_to, passes, history: newHistory, fired, cooldown, propG };
}

// ─── Orbit: fire ONLY at perigee ──────────────────────────────────────────────
function makeOrbit(alt_km) {
  const r = RE + alt_km * 1e3;
  const vc = Math.sqrt(MU / r);
  return {
    x: r, y: 0, vx: 0, vy: vc,
    apogee: alt_km, perigee: alt_km,
    passes: 0, propG: 0,
    history: [{ n: 0, apo: alt_km }],
    fired: false, prevVr: -1,
    trail: []
  };
}

function stepOrbit(s, dt, J) {
  let { x, y, vx, vy, passes, propG, history, prevVr, trail } = s;
  let fired = false;

  const r = Math.sqrt(x * x + y * y);
  const vr = (x * vx + y * vy) / r;

  // Fire at perigee: radial velocity crosses zero from negative to positive
  if (prevVr < 0 && vr >= 0 && J > 0) {
    const v = Math.sqrt(vx * vx + vy * vy);
    const dv = J / M_SAT;
    vx += dv * (vx / v);
    vy += dv * (vy / v);
    fired = true;
    passes++;
    const dm = M_SAT * (1 - Math.exp(-dv / (ISP_CG * G0)));
    propG += dm * 1000;
  }

  // Verlet
  const ax = -MU * x / (r * r * r);
  const ay = -MU * y / (r * r * r);
  const nx = x + vx * dt + 0.5 * ax * dt * dt;
  const ny = y + vy * dt + 0.5 * ay * dt * dt;
  const nvx = vx + ax * dt;
  const nvy = vy + ay * dt;

  // Orbital elements
  const nr = Math.sqrt(nx * nx + ny * ny);
  const nv2 = nvx * nvx + nvy * nvy;
  const eps = 0.5 * nv2 - MU / nr;
  const a = -MU / (2 * eps);
  const hv = nx * nvy - ny * nvx;
  const ex = (nvy * hv) / MU - nx / nr;
  const ey = (-nvx * hv) / MU - ny / nr;
  const e = Math.sqrt(ex * ex + ey * ey);
  const apo = (a * (1 + e) - RE) / 1e3;
  const peri = (a * (1 - e) - RE) / 1e3;

  // Trail (keep last 120 points)
  const newTrail = [...trail.slice(-119), { x: nx, y: ny }];

  const newHistory = fired
    ? [...history.slice(-79), { n: passes, apo }]
    : history;

  return {
    ...s, x: nx, y: ny, vx: nvx, vy: nvy,
    apogee: apo, perigee: peri,
    passes, propG, history: newHistory,
    fired, prevVr: (nx * nvx + ny * nvy) / nr,
    trail: newTrail
  };
}

// ─── Spark line ───────────────────────────────────────────────────────────────
function Spark({ data, yKey, color, label, unit }) {
  if (data.length < 2) return null;
  const vals = data.map(d => d[yKey]);
  const lo = Math.min(...vals), hi = Math.max(...vals), rng = hi - lo || 0.001;
  const W = 200, H = 44;
  const pts = vals.map((v, i) =>
    `${(i / (vals.length - 1)) * W},${H - 3 - ((v - lo) / rng) * (H - 6)}`
  ).join(' ');
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 9, color: '#4a7a6a', marginBottom: 2 }}>
        {label}: <span style={{ color, fontWeight: 'bold' }}>
          {vals[vals.length - 1].toFixed(3)}{unit}
        </span>
      </div>
      <svg width={W} height={H}>
        <polyline points={pts} fill="none" stroke={color}
          strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle
          cx={(vals.length - 1) / (vals.length - 1) * W}
          cy={H - 3 - ((vals[vals.length - 1] - lo) / rng) * (H - 6)}
          r="3" fill={color} />
      </svg>
    </div>
  );
}

// ─── Pendulum Canvas ──────────────────────────────────────────────────────────
export function PendCanvas({ s, W = 320, H = 300, style = {} }) {
  const cx = W / 2, py = 36;
  const sc = (H - 90) / L;
  const bx = cx + L * Math.sin(s.theta) * sc;
  const by = py + L * Math.cos(s.theta) * sc;
  const floor_y = py + L * sc;

  // Max TO-side position (ghost)
  const maxAngle = Math.acos(Math.max(-1, 1 - s.maxH_to / L));
  const gx = cx + L * Math.sin(maxAngle) * sc;
  const gy = py + L * Math.cos(maxAngle) * sc;

  const acc = '#0d9e6a', hi = '#00ff88', dim = '#1a4a3a', mut = '#3a6a5a';

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', ...style }}>
      {/* ceiling */}
      <rect x={cx - 24} y={22} width={48} height={14} rx={3} fill={dim} />
      <line x1={cx - 32} y1={34} x2={cx + 32} y2={34} stroke={mut} strokeWidth="2" />

      {/* equilibrium dashed */}
      <line x1={cx} y1={36} x2={cx} y2={floor_y + 6}
        stroke={dim} strokeWidth="0.5" strokeDasharray="3 4" />

      {/* TO label */}
      <text x={cx + 60} y={floor_y - 20} fill={mut}
        fontSize="9" fontFamily="monospace">TO →</text>
      <text x={cx - 60} y={floor_y - 20} fill={dim}
        fontSize="9" fontFamily="monospace" textAnchor="end">← FRO</text>

      {/* ghost max TO position */}
      <line x1={cx} y1={36} x2={gx} y2={gy}
        stroke={dim} strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
      <circle cx={gx} cy={gy} r={10} fill="none"
        stroke={hi} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5" />

      {/* height bar — right side */}
      <line x1={cx + 100} y1={floor_y} x2={cx + 100} y2={floor_y - s.maxH_to * sc}
        stroke={mut} strokeWidth="0.5" />
      <line x1={cx + 96} y1={floor_y - s.maxH_to * sc}
        x2={cx + 104} y2={floor_y - s.maxH_to * sc}
        stroke={hi} strokeWidth="1.2" />
      <text x={cx + 107} y={floor_y - s.maxH_to * sc}
        fill={hi} fontSize="9" fontFamily="monospace" dominantBaseline="middle">
        {(s.maxH_to * 100).toFixed(2)}cm
      </text>

      {/* floor */}
      <line x1={16} y1={floor_y + 4} x2={W - 16} y2={floor_y + 4}
        stroke={dim} strokeWidth="0.5" />

      {/* wire */}
      <line x1={cx} y1={36} x2={bx} y2={by}
        stroke={s.fired ? hi : mut} strokeWidth="1.5" />

      {/* bob */}
      <circle cx={bx} cy={by} r={15}
        fill={s.fired ? '#003820' : '#060f0a'}
        stroke={s.fired ? hi : acc}
        strokeWidth={s.fired ? 2.5 : 1.5} />
      <circle cx={bx} cy={by} r={6}
        fill={s.fired ? hi : acc} opacity={s.fired ? 1 : 0.6} />

      {/* fire ring */}
      {s.fired && <>
        <circle cx={bx} cy={by} r={26} fill="none"
          stroke={hi} strokeWidth="1" opacity="0.4" />
        <text x={bx} y={by - 34} fill={hi}
          fontSize="9" fontFamily="monospace" textAnchor="middle">
          REPULSION ↑
        </text>
      </>}

      {/* pivot */}
      <circle cx={cx} cy={36} r={4} fill="#445" />
    </svg>
  );
}

// ─── Orbit Canvas (close-up) ──────────────────────────────────────────────────
function OrbitCanvas({ s, W = 320, H = 300 }) {
  const cx = W / 2, cy = H / 2;

  // Scale so Earth nearly fills, orbit is visible as ring around edge
  const r_base = RE + 450e3;
  const sc = (Math.min(W, H) * 0.42) / r_base;
  const er = RE * sc;

  const sx = cx + s.x * sc;
  const sy = cy - s.y * sc;

  // Ellipse from current orbital elements
  const r_apo = (RE + s.apogee * 1e3) * sc;
  const r_peri = (RE + s.perigee * 1e3) * sc;
  const a_px = (r_apo + r_peri) / 2;
  const c_px = a_px - r_peri;
  const b_px = Math.sqrt(Math.max(1, a_px * a_px - c_px * c_px));

  const r_target = (RE + 460e3) * sc;
  const r_init = (RE + 450e3) * sc;

  // Trail
  const trailPts = s.trail.map(p =>
    `${cx + p.x * sc},${cy - p.y * sc}`).join(' ');

  const acc = '#0d9e6a', hi = '#00ff88', dim = '#0a2a1a', mut = '#2a5a3a';

  return (
    <svg width={W} height={H} style={{ display: 'block' }}>
      {/* initial orbit ring */}
      <circle cx={cx} cy={cy} r={r_init} fill="none"
        stroke={dim} strokeWidth="0.6" strokeDasharray="2 4" />

      {/* target orbit ring */}
      <circle cx={cx} cy={cy} r={r_target} fill="none"
        stroke="#1a4a2a" strokeWidth="0.8" strokeDasharray="3 3" />
      <text x={cx + r_target + 2} y={cy - 2} fill="#1a6a3a"
        fontSize="7" fontFamily="monospace">460km</text>

      {/* Earth */}
      <circle cx={cx} cy={cy} r={er} fill="#060f14" stroke="#0a2a3e" strokeWidth="1" />
      <text x={cx} y={cy + 3} fill="#0a3a5e"
        fontSize="8" fontFamily="monospace" textAnchor="middle">EARTH</text>

      {/* trail */}
      {s.trail.length > 1 &&
        <polyline points={trailPts} fill="none"
          stroke={acc} strokeWidth="0.6" opacity="0.3" />}

      {/* current orbit ellipse */}
      <ellipse cx={cx - c_px} cy={cy} rx={a_px} ry={b_px}
        fill="none" stroke={acc} strokeWidth="1.2" />

      {/* perigee dot */}
      <circle cx={cx + r_peri} cy={cy} r={3} fill={mut} />
      <text x={cx + r_peri + 3} y={cy - 5} fill={mut}
        fontSize="7" fontFamily="monospace">{s.perigee.toFixed(1)}km</text>

      {/* apogee dot */}
      <circle cx={cx - r_apo} cy={cy} r={4}
        fill={s.fired ? hi : acc} />
      <text x={cx - r_apo - 2} y={cy - 8} fill={s.fired ? hi : acc}
        fontSize="8" fontFamily="monospace" textAnchor="end">
        {s.apogee.toFixed(2)}km
      </text>

      {/* satellite */}
      <circle cx={sx} cy={sy} r={4}
        fill={s.fired ? hi : acc}
        stroke={s.fired ? hi : mut}
        strokeWidth={s.fired ? 2 : 1} />
      {s.fired && <>
        <circle cx={sx} cy={sy} r={10} fill="none"
          stroke={hi} strokeWidth="1" opacity="0.5" />
        <text x={sx + 13} y={sy - 3} fill={hi}
          fontSize="7" fontFamily="monospace">BURN</text>
      </>}
    </svg>
  );
}

// ─── Propellant comparison bar ────────────────────────────────────────────────
function PropBar({ label, grams, maxG, color, sub }) {
  const pct = Math.min(100, (grams / maxG) * 100);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 3 }}>
        <span style={{ color: '#8ab' }}>{label}</span>
        <span style={{ color, fontFamily: 'monospace', fontWeight: 'bold' }}>{grams.toFixed(3)}g</span>
      </div>
      <div style={{ background: '#0a1a14', borderRadius: 3, height: 10, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color,
          borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <div style={{ fontSize: 8, color: '#3a6a5a', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('sim');
  const [J, setJ] = useState(0.313);
  const [beta, setBeta] = useState(0.005);

  const [pend, setPend] = useState(makePend);
  const [orbit, setOrbit] = useState(() => makeOrbit(450));
  // Conventional orbit: same burns but 54% wasted on attitude
  const [convOrbit, setConvOrbit] = useState(() => makeOrbit(450));

  const rafRef = useRef(), lastRef = useRef(null);
  const pRef = useRef(pend), oRef = useRef(orbit), cRef = useRef(convOrbit);
  pRef.current = pend; oRef.current = orbit; cRef.current = convOrbit;

  const reset = useCallback(() => {
    setRunning(false);
    setPend(makePend());
    setOrbit(makeOrbit(450));
    setConvOrbit(makeOrbit(450));
  }, []);

  useEffect(() => {
    if (!running) { cancelAnimationFrame(rafRef.current); return; }
    const tick = ts => {
      if (lastRef.current === null) lastRef.current = ts;
      const wdt = Math.min((ts - lastRef.current) / 1000, 0.05);
      lastRef.current = ts;

      // Pendulum: real time, 20 sub-steps
      let ps = pRef.current;
      for (let i = 0; i < 20; i++) ps = stepPend(ps, wdt / 20, J, beta);
      setPend(ps);

      // EP-ADCS orbit: 100× compression, 300 sub-steps
      const odt = (wdt * 100) / 300;
      let os = oRef.current;
      for (let i = 0; i < 300; i++) os = stepOrbit(os, odt, J);
      setOrbit(os);

      // Conventional orbit: same J but propellant is 1/0.46 × useful
      // We simulate by tracking propG separately at 54% waste ratio
      let cs = cRef.current;
      for (let i = 0; i < 300; i++) {
        cs = stepOrbit(cs, odt, J);
        // propG already tracks useful burns; multiply for conventional
      }
      // conventional propG = EP propG / 0.46
      const convPropG = os.propG / 0.46;
      setConvOrbit({ ...cs, propG: convPropG });

      rafRef.current = requestAnimationFrame(tick);
    };
    lastRef.current = null;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running, J, beta]);

  // Derived propellant numbers
  const epProp = orbit.propG;
  const convProp = epProp > 0 ? epProp / 0.46 : 0;
  const savedProp = convProp - epProp;
  const savingPct = convProp > 0 ? (savedProp / convProp * 100) : 54;
  const maxProp = Math.max(convProp, 0.001);

  // Full raise numbers (450→460km)
  const dv_total = 5.597; // m/s
  const ep_full = M_SAT * (1 - Math.exp(-dv_total / (ISP_CG * G0))) * 1000;
  const conv_full = ep_full / 0.46;
  const green_full = M_SAT * (1 - Math.exp(-dv_total / (250 * G0))) * 1000 / 0.60;
  const ion_full = M_SAT * (1 - Math.exp(-dv_total / (1500 * G0))) * 1000 / 0.80;

  const bg = '#050e10', card = '#0b1a1e', bord = '#1a3a30';
  const acc = '#0d9e6a', hi = '#00ff88', mut = '#4a7a6a', txt = '#b0d0c0';

  const tabs = ['sim', 'propellant'];
  const tlabels = { sim: 'Live Sim', propellant: 'Propellant Saved' };

  return (
    <div style={{ background: bg, minHeight: '100vh', color: txt,
      fontFamily: "'Courier New',monospace", padding: 14 }}>

      {/* header */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 9, color: mut, letterSpacing: '0.2em', marginBottom: 2 }}>
          SPACEMANCRAFT · EP-ADCS
        </div>
        <div style={{ fontSize: 16, color: hi }}>Pendulum → Orbit Raise</div>
        <div style={{ fontSize: 9, color: mut, marginTop: 1 }}>
          TO-side repulsion only · ±15° · 1cm raise per stroke
        </div>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, justifyContent: 'center' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab === t ? acc : 'transparent',
            color: tab === t ? '#000' : mut,
            border: `1px solid ${tab === t ? acc : bord}`,
            borderRadius: 3, padding: '3px 16px',
            cursor: 'pointer', fontSize: 9, letterSpacing: '0.08em'
          }}>{tlabels[t]}</button>
        ))}
      </div>

      {tab === 'sim' && <>
        {/* controls */}
        <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 5,
          padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 8, color: mut, marginBottom: 3, letterSpacing: '0.1em' }}>
                REPULSION IMPULSE J (Ns)
              </div>
              <input type="range" min="0.1" max="0.6" step="0.01" value={J}
                onChange={e => setJ(+e.target.value)}
                style={{ width: '100%', accentColor: acc }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9 }}>
                <span style={{ color: acc }}>{J.toFixed(3)} Ns</span>
                <span style={{ color: mut }}>
                  Δh={(J * J / (2 * M_BOB * G) * 100).toFixed(2)}cm/stroke
                </span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 8, color: mut, marginBottom: 3, letterSpacing: '0.1em' }}>
                DAMPING β (air resistance)
              </div>
              <input type="range" min="0.001" max="0.015" step="0.001" value={beta}
                onChange={e => setBeta(+e.target.value)}
                style={{ width: '100%', accentColor: acc }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9 }}>
                <span style={{ color: acc }}>{beta.toFixed(3)}</span>
                <span style={{ color: mut }}>
                  equilibrium={(J * J / (2 * M_BOB * G * (beta + 0.002)) * 100).toFixed(1)}cm
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* play */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, justifyContent: 'center' }}>
          <button onClick={() => setRunning(r => !r)} style={{
            background: running ? '#0a2a1a' : acc,
            color: running ? hi : '#000',
            border: `1px solid ${running ? hi : acc}`,
            borderRadius: 3, padding: '6px 24px',
            cursor: 'pointer', fontSize: 10, letterSpacing: '0.08em'
          }}>{running ? '⏸ PAUSE' : '▶ RUN'}</button>
          <button onClick={reset} style={{
            background: 'transparent', color: mut,
            border: `1px solid ${bord}`, borderRadius: 3,
            padding: '6px 14px', cursor: 'pointer', fontSize: 10
          }}>RESET</button>
        </div>

        {/* side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>

          {/* Pendulum panel */}
          <div style={{ background: card, border: `1px solid ${pend.fired ? hi : bord}`,
            borderRadius: 5, overflow: 'hidden', transition: 'border-color 0.1s' }}>
            <div style={{ padding: '5px 10px', borderBottom: `1px solid ${bord}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 8, color: mut, letterSpacing: '0.12em' }}>
                PENDULUM L=2.34m
              </div>
              <div style={{ fontSize: 8, color: pend.fired ? hi : mut }}>
                {pend.fired ? '◉ FIRING' : '○ SWING'}
              </div>
            </div>
            <PendCanvas s={pend} W={300} H={270} />
            <div style={{ padding: '6px 10px', borderTop: `1px solid ${bord}` }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, fontSize: 9 }}>
                <div>
                  <span style={{ color: mut }}>height </span>
                  <span style={{ color: acc }}>{(pend.h * 100).toFixed(2)}cm</span>
                </div>
                <div>
                  <span style={{ color: mut }}>TO-max </span>
                  <span style={{ color: hi }}>{(pend.maxH_to * 100).toFixed(2)}cm</span>
                </div>
                <div>
                  <span style={{ color: mut }}>strokes </span>
                  <span style={{ color: hi }}>{pend.passes}</span>
                </div>
              </div>
              <Spark data={pend.history} yKey="h" color={hi}
                label="TO amplitude" unit="cm" />
            </div>
          </div>

          {/* Orbit panel */}
          <div style={{ background: card, border: `1px solid ${orbit.fired ? hi : bord}`,
            borderRadius: 5, overflow: 'hidden', transition: 'border-color 0.1s' }}>
            <div style={{ padding: '5px 10px', borderBottom: `1px solid ${bord}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 8, color: mut, letterSpacing: '0.12em' }}>
                ORBIT 450→460km (100×)
              </div>
              <div style={{ fontSize: 8, color: orbit.fired ? hi : mut }}>
                {orbit.fired ? '◉ BURN AT PERIGEE' : '○ COASTING'}
              </div>
            </div>
            <OrbitCanvas s={orbit} W={300} H={270} />
            <div style={{ padding: '6px 10px', borderTop: `1px solid ${bord}` }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, fontSize: 9 }}>
                <div>
                  <span style={{ color: mut }}>perigee </span>
                  <span style={{ color: acc }}>{orbit.perigee.toFixed(1)}km</span>
                </div>
                <div>
                  <span style={{ color: mut }}>apogee </span>
                  <span style={{ color: hi }}>{orbit.apogee.toFixed(2)}km</span>
                </div>
                <div>
                  <span style={{ color: mut }}>burns </span>
                  <span style={{ color: hi }}>{orbit.passes}</span>
                </div>
              </div>
              <Spark data={orbit.history} yKey="apo" color={hi}
                label="apogee altitude" unit="km" />
            </div>
          </div>
        </div>

        {/* mapping strip */}
        <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 5,
          padding: '8px 12px' }}>
          <div style={{ fontSize: 8, color: mut, letterSpacing: '0.15em', marginBottom: 6 }}>
            THE EXACT PARALLEL
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
            gap: 4, fontSize: 9 }}>
            {[
              ['PENDULUM', 'ORBIT', ''],
              ['TO turning point', 'Perigee', 'Fire here only'],
              ['Bob stationary', 'v_radial = 0', 'Zero velocity point'],
              [`+${(J*J/(2*M_BOB*G)*100).toFixed(2)}cm per stroke`, `+${(2*(RE+450e3)*(J/M_SAT)/Math.sqrt(MU/(RE+450e3))).toFixed(1)}m per pass`, 'Grows every cycle'],
              ['Gravity stores height', 'Gravity stores altitude', 'Same storage'],
              ['FRO: no firing', 'Apogee: no firing', 'Coasting phase'],
              ['Amplitude grows', 'Apogee grows', 'Resonant accumulation'],
            ].map(([a, b, c], i) => (
              <div key={i} style={{
                padding: '2px 4px',
                color: i === 0 ? mut : i % 2 === 0 ? '#6a9a7a' : txt,
                fontWeight: i === 0 ? 'bold' : 'normal',
                borderBottom: `1px solid ${bord}`
              }}>
                {i === 0 ? a : a}
              </div>
            ))}
          </div>
        </div>
      </>}

      {tab === 'propellant' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* live propellant */}
          <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 5, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: hi, letterSpacing: '0.1em', marginBottom: 8 }}>
              LIVE — PROPELLANT USED SO FAR
            </div>
            <PropBar label="EP-ADCS (your system)"
              grams={epProp} maxG={Math.max(convProp, 0.01)}
              color={acc} sub={`Isp=70s · EMMS eliminates attitude waste · ${orbit.passes} burns`} />
            <PropBar label="Conventional cold gas (same thruster, no EMMS)"
              grams={convProp} maxG={Math.max(convProp, 0.01)}
              color="#e24a4a" sub="54% of propellant wasted correcting attitude disturbance" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10 }}>
              {[
                { label: 'EP-ADCS used', val: epProp.toFixed(4) + 'g', color: acc },
                { label: 'Conventional would use', val: convProp.toFixed(4) + 'g', color: '#e24a4a' },
                { label: 'Saved', val: savedProp.toFixed(4) + 'g (' + savingPct.toFixed(1) + '%)', color: hi },
              ].map(({ label, val, color }) => (
                <div key={label} style={{ background: '#050e10', borderRadius: 4, padding: '6px 8px' }}>
                  <div style={{ fontSize: 8, color: mut, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, color, fontWeight: 'bold', fontFamily: 'monospace' }}>{val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* full raise comparison */}
          <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 5, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: hi, letterSpacing: '0.1em', marginBottom: 8 }}>
              FULL 450→460km RAISE — ALL SYSTEMS COMPARED
            </div>
            <div style={{ fontSize: 8, color: mut, marginBottom: 6 }}>
              Total ΔV = 5.597 m/s · Satellite mass = 12kg
            </div>

            {[
              { name: 'EP-ADCS (cold gas + EMMS)', isp: 70, prop: ep_full, waste: 5,
                power: '<1W', feasible: true, yours: true,
                note: 'EMMS absorbs reaction → zero attitude correction burns' },
              { name: 'Conventional cold gas', isp: 70, prop: conv_full, waste: 54,
                power: '<1W', feasible: true, yours: false,
                note: '54% of propellant wasted firing attitude corrections after each burn' },
              { name: 'Green monopropellant (AF-M315E)', isp: 250, prop: green_full, waste: 40,
                power: '<3W', feasible: true, yours: false,
                note: 'Better Isp but still needs attitude correction — less total saving' },
              { name: 'Ion thruster', isp: 1500, prop: ion_full, waste: 20,
                power: '80W+', feasible: false, yours: false,
                note: 'Best propellant mass but needs 80W — impossible on 12U (max 20W)' },
            ].map((r) => {
              const barW = Math.min(100, (r.prop / conv_full) * 100);
              const color = r.yours ? acc : r.feasible ? '#e2884a' : '#e24a4a';
              return (
                <div key={r.name} style={{
                  background: r.yours ? '#0a2a1a' : '#080e12',
                  border: `1px solid ${r.yours ? acc : bord}`,
                  borderRadius: 4, padding: '8px 10px', marginBottom: 6
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontSize: 9, color: r.yours ? hi : '#8ab' }}>
                      {r.yours && '★ '}{r.name}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.yours && <span style={{ fontSize: 7, background: acc, color: '#000',
                        padding: '1px 6px', borderRadius: 8 }}>YOUR SYSTEM</span>}
                      {!r.feasible && <span style={{ fontSize: 7, background: '#2a0a0a', color: '#e24a4a',
                        padding: '1px 6px', borderRadius: 8 }}>NOT FEASIBLE 12U</span>}
                    </div>
                  </div>
                  <div style={{ background: '#050e10', borderRadius: 2, height: 8, marginBottom: 5 }}>
                    <div style={{ width: `${barW}%`, height: '100%',
                      background: color, borderRadius: 2, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, fontSize: 8 }}>
                    <div><span style={{ color: mut }}>Isp </span>
                      <span style={{ color: txt }}>{r.isp}s</span></div>
                    <div><span style={{ color: mut }}>Prop </span>
                      <span style={{ color, fontWeight: 'bold' }}>{r.prop.toFixed(1)}g</span></div>
                    <div><span style={{ color: mut }}>Waste </span>
                      <span style={{ color: r.waste > 30 ? '#e24a4a' : acc }}>{r.waste}%</span></div>
                    <div><span style={{ color: mut }}>Power </span>
                      <span style={{ color: r.feasible ? txt : '#e24a4a' }}>{r.power}</span></div>
                  </div>
                  <div style={{ fontSize: 8, color: '#4a7a6a', marginTop: 4 }}>{r.note}</div>
                </div>
              );
            })}
          </div>

          {/* why EMMS saves propellant */}
          <div style={{ background: card, border: `1px solid ${bord}`, borderRadius: 5, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: hi, letterSpacing: '0.1em', marginBottom: 8 }}>
              WHY EP-ADCS SAVES 54% PROPELLANT — THE MECHANISM
            </div>
            {[
              {
                step: '1', title: 'Conventional thruster fires',
                body: 'Thrust vector never passes exactly through centre of mass.\nTorque = Force × offset distance\nSatellite starts rotating after every burn.',
                color: '#e24a4a'
              },
              {
                step: '2', title: 'Attitude correction burn needed',
                body: 'Must fire a second thruster to stop the rotation.\nThis correction burn consumes propellant.\n54% of all propellant goes to these corrections — not to raising orbit.',
                color: '#e2884a'
              },
              {
                step: '3', title: 'EP-ADCS: EMMS absorbs the torque',
                body: 'Cold gas fires on the moving mass inside the satellite.\nMoving mass rides the track — reaction goes into track, not hull.\nHull does not rotate. Zero correction burn needed.',
                color: acc
              },
              {
                step: '4', title: 'Pendulum experiment proved this',
                body: 'Your 1cm raise per TO-side stroke = 100% efficiency.\nZero energy wasted on wrong-direction pushes.\nIn orbit: zero propellant wasted on attitude correction.',
                color: hi
              },
            ].map(r => (
              <div key={r.step} style={{ display: 'flex', gap: 10, marginBottom: 8,
                paddingBottom: 8, borderBottom: `1px solid ${bord}` }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  background: r.color, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 10, color: '#000', fontWeight: 'bold' }}>
                  {r.step}
                </div>
                <div>
                  <div style={{ fontSize: 9, color: r.color, marginBottom: 3 }}>{r.title}</div>
                  <pre style={{ fontSize: 9, color: txt, margin: 0, lineHeight: 1.7,
                    whiteSpace: 'pre-wrap', fontFamily: "'Courier New',monospace" }}>
                    {r.body}
                  </pre>
                </div>
              </div>
            ))}

            <div style={{ background: '#0a2a1a', border: `1px solid ${acc}`,
              borderRadius: 4, padding: '8px 10px', marginTop: 4 }}>
              <div style={{ fontSize: 9, color: hi, marginBottom: 4 }}>BOTTOM LINE</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 10 }}>
                <div style={{ color: txt }}>
                  Full 450→460km raise:<br />
                  <span style={{ color: acc, fontWeight: 'bold' }}>EP-ADCS: {ep_full.toFixed(1)}g</span>
                </div>
                <div style={{ color: txt }}>
                  Same raise, conventional:<br />
                  <span style={{ color: '#e24a4a', fontWeight: 'bold' }}>Conv: {conv_full.toFixed(1)}g</span>
                </div>
                <div style={{ color: txt, gridColumn: '1/-1' }}>
                  Saving: <span style={{ color: hi, fontWeight: 'bold', fontSize: 13 }}>
                    {(conv_full - ep_full).toFixed(1)}g ({((conv_full - ep_full) / conv_full * 100).toFixed(1)}%)
                  </span>
                  <span style={{ color: mut, fontSize: 8 }}> — on a 12kg satellite, this is enormous</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 12, fontSize: 8, color: mut }}>
        SPACEMANCRAFT PVT LTD · EP-ADCS · RESONANT ORBIT RAISING
      </div>
    </div>
  );
}